import { sql } from 'drizzle-orm';
import { index, jsonb, pgTable, text, timestamp, uniqueIndex, uuid, varchar } from 'drizzle-orm/pg-core';
import { id } from './_shared';
import { users } from './users';

// =============================================================================
// JOB SCORE FACETS — Class 10, THE RESCORING ENGINE (D21). docs/design/
// fix-architecture-2026-07-08/{FIX-ARCHITECTURE.md,BUILD-PLAN.md}, Phase 2
// Lane E. SQL twin: docs/design/fix-architecture-2026-07-08/
// phase2a-declared-answers-neon.sql.
//
// Jason, verbatim (03:01 EDT, the refinement that supersedes a bare
// undetermined/null): "It has enough to actually score. What's not happening
// is it's not RESCORING and REANALYZING based off the additional information
// — it's trying to flatten it... Instead of saying 'I don't know' it should
// say 'I DO know — this is what I know SO FAR' — and maybe not give a
// straight number yet. And it could go into a different bucket of ones to
// EXPLORE, not fully scored."
//
// THIS TABLE is the keystone hub's shape (corpus_coverage_state) APPLIED to a
// per-job CONFIDENCE signal instead of a per-topic coverage signal — same
// "small additive table, one row per compiled key, upserted by a hub-write
// unit, read by a hub-read unit" family. One row per (user, job, scenario):
// jobId stays TEXT to match job_scores.jobId's own un-migrated shape (some
// rows are legacy `jobs.id`, others are the newer lake `user_jobs`-linked
// uuid-as-text — see job_scores.ts's own header on userJobId) rather than a
// FK, since job_scores itself carries no FK on jobId either.
//
// NO INVENTION: knownFacets/pendingFacets are STRUCTURED, COUNTABLE facts
// (which named signal contributed or is still missing) — never a generated
// sentence. A caller (JobCard's honest chip, the metrics table) builds its
// OWN grounded copy from these fields, exactly like CoverageAlignmentNote.
// =============================================================================

/** The named facets a job's fit score can draw on. Countable, not prose —
 *  mirrors RadarAxes' own named-signal shape (jobMetrics.ts). Extend this
 *  list additively as new scoring inputs land; never repurpose an existing
 *  key's meaning. */
export const SCORE_FACET_KEYS = [
  'jd_title_read',
  'jd_comp_band',
  'jd_location_read',
  'corpus_depth',
  'declared_home_base',
  'declared_direction',
] as const;

export type ScoreFacetKey = (typeof SCORE_FACET_KEYS)[number];

/** The tri-state a job's fit score renders as — widens jobMetrics.ts's
 *  existing binary 'scored'/'undetermined'/'unscored' (JobMetricRow.
 *  fitDetermination, UNCHANGED and still the read-model's own contract) with
 *  a fourth, explicitly PROVISIONAL state that carries what's known so far
 *  rather than collapsing to a bare null. 'provisional' is this table's own
 *  addition; the other three keep jobMetrics.ts's existing meaning verbatim. */
export type ScoreConfidenceState = 'scored' | 'provisional' | 'not_yet_attempted';

/** job_scores.scenarioId is nullable (a legacy/no-scenario read); a unique
 *  index cannot dedupe on NULL (Postgres treats every NULL as distinct), so a
 *  "no scenario selected" row is stored under this explicit sentinel instead
 *  of a bare null — the same problem the eventual default-view fix (Decision
 *  11) will also have to name honestly. Never a real scenario id. */
export const NO_SCENARIO_SENTINEL = '__no_scenario__';

export const jobScoreFacets = pgTable(
  'job_score_facets',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    // TEXT, mirrors job_scores.jobId — see header for why this is not a uuid FK.
    jobId: text('job_id').notNull(),
    // NOT NULL — see NO_SCENARIO_SENTINEL's own doc comment for why a bare
    // null can't be used here even though job_scores.scenarioId itself is
    // nullable.
    scenarioId: text('scenario_id').notNull().default(NO_SCENARIO_SENTINEL),
    state: varchar('state', { length: 24 }).notNull(),
    knownFacets: jsonb('known_facets').$type<ScoreFacetKey[]>().notNull().default(sql`'[]'::jsonb`),
    pendingFacets: jsonb('pending_facets').$type<ScoreFacetKey[]>().notNull().default(sql`'[]'::jsonb`),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    // One compiled row per (user, job, scenario) — a NULL scenarioId is its
    // own distinct key (the no-scenario-selected read), matching job_scores'
    // own nullable scenarioId precedent.
    userJobScenarioIdx: uniqueIndex('job_score_facets_user_job_scenario_idx').on(
      t.userId,
      t.jobId,
      t.scenarioId,
    ),
    userIdx: index('job_score_facets_user_idx').on(t.userId),
  }),
);
